import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';

/**
 * Der Monat als Leiste über der Woche.
 *
 * Sie beantwortet die Frage, die es vorher gar nicht gab: ab wann gelten
 * meine Zahlen? Bisher konnte man erfassen und korrigieren, ohne dass je
 * ein Moment eintrat, ab dem etwas fest war - und ohne dass eine
 * vorgesetzte Person je darauf geschaut hätte.
 *
 * Die Leiste zeigt deshalb immer beides: den Zustand und den Weg zum
 * nächsten. Ein blosses Abzeichen "eingereicht" liesse offen, was jetzt
 * passieren muss.
 */

const VARIANTE = {
  DRAFT: 'neutral',
  SUBMITTED: 'info',
  APPROVED: 'success',
  REJECTED: 'danger',
};

/** "-2.40" statt "-2.4" - und ein Plus bekommt sein Zeichen. */
function signed(value) {
  const n = Number(value ?? 0);
  return `${n > 0 ? '+' : ''}${n.toFixed(2)}`;
}

export function MonthBar({ month, onSubmit, onWithdraw, busy = false }) {
  const { t } = useTranslation();

  if (!month) {
    return null;
  }

  const status = month.status || 'DRAFT';
  const saldo = Number(month.balanceHours ?? 0);
  /* Offen heisst hier: es lässt sich noch etwas ändern. Eine Zurückweisung
     gehört dazu - sie fordert zur Korrektur auf, und ein weiterhin
     gesperrter Monat machte genau die unmöglich. */
  const offen = status === 'DRAFT' || status === 'REJECTED';

  return (
    <div className="month-bar" data-status={status}>
      <div className="month-bar-head">
        <span className="month-bar-label">
          {t(`month.name.${month.month}`)} {month.year}
        </span>
        <Badge variant={VARIANTE[status]}>{t(`month.status.${status}`)}</Badge>
      </div>

      <dl className="month-bar-figures">
        <div>
          <dt>{t('month.target')}</dt>
          <dd className="num mono">{Number(month.targetHours ?? 0).toFixed(2)}</dd>
        </div>
        <div>
          <dt>{t('month.actual')}</dt>
          <dd className="num mono">{Number(month.actualHours ?? 0).toFixed(2)}</dd>
        </div>
        <div data-sign={saldo > 0 ? 'plus' : saldo < 0 ? 'minus' : undefined}>
          <dt>{t('month.balance')}</dt>
          <dd className="num mono">{signed(saldo)}</dd>
        </div>
      </dl>

      <div className="month-bar-action">
        {offen && (
          <Button variant="primary" size="sm" onClick={onSubmit} disabled={busy}>
            {t('month.submit')}
          </Button>
        )}
        {status === 'SUBMITTED' && (
          <>
            <span className="muted text-sm">{t('month.waiting')}</span>
            <Button variant="ghost" size="sm" onClick={onWithdraw} disabled={busy}>
              {t('month.withdraw')}
            </Button>
          </>
        )}
        {status === 'APPROVED' && <span className="muted text-sm">{t('month.approvedHint')}</span>}
      </div>

      {/* Der Grund einer Zurückweisung ist die einzige Angabe, wegen der
          jemand diese Leiste zweimal liest. Deshalb steht er ausgeschrieben
          da und nicht hinter einem Aufklappen. */}
      {status === 'REJECTED' && month.decisionNote && (
        <p className="month-bar-note">{t('month.rejectedNote', { note: month.decisionNote })}</p>
      )}
    </div>
  );
}
